import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import request from 'lib/request';
import type { ReferenceLocationResponse } from 'types/api-responses';
import { addSurveyArea, removeSurveyArea, type SurveyRow } from './survey-rows';
import type { SurveyAreaCandidate } from 'components/query-map/map-pick-control';
import { narrowSurveyAreaCandidates } from './narrow-survey-candidates';

export interface SurveyAreaPick {
  /** True while a picked section's record is being read. */
  loading: boolean;
  /** Set when a pick couldn't be turned into a row. */
  notice: string | null;
  /**
   * The overlapping survey areas a click landed in, when more than one survives
   * narrowing. Empty otherwise; the host offers these to choose between.
   */
  choices: SurveyAreaCandidate[];
  /** Toggles the clicked survey area in or out of the rows. */
  pick: (candidates: SurveyAreaCandidate[]) => void;
  /** Resolves an overlap: adds the chosen area and clears the choice. */
  choose: (candidate: SurveyAreaCandidate) => void;
  dismiss: () => void;
}

/**
 * Turns a click on the survey grid into a survey row, and a click on a picked
 * section into its removal. An unresolvable click reports what it found rather
 * than guessing a boundary nobody asked for.
 *
 * @param {SurveyRow[]} rows - The current rows, read to decide add vs remove.
 * @param {Function} setRows - The rows setter.
 * @return {SurveyAreaPick} The pick handler and its state.
 */
export const useSurveyAreaPick = (
  rows: SurveyRow[],
  setRows: Dispatch<SetStateAction<SurveyRow[]>>
): SurveyAreaPick => {
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [choices, setChoices] = useState<SurveyAreaCandidate[]>([]);

  // The handler is registered with the map, so it has to keep one identity for
  // as long as the panel is open — it reads the current rows through a ref
  // rather than closing over them.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // The panel can close mid-request, and the reply would land on nothing.
  const abort = useRef(new AbortController());
  useEffect(() => {
    const controller = abort.current;
    return () => controller.abort();
  }, []);

  /**
   * Reads one survey area and adds it as a row. Shared by a plain pick and by
   * resolving an overlap, so both end the same way.
   */
  const addById = useCallback(
    async (id: number) => {
      setLoading(true);
      const response = await request.get<ReferenceLocationResponse>(
        `/reference_locations/${id}`,
        abort.current
      );

      // An aborted read means the panel closed; there is nobody to tell.
      if (response && 'error' in response && response.error === 'AbortError') {
        return;
      }
      setLoading(false);

      if (!response || 'error' in response) {
        setNotice('Could not read that survey area.');
        return;
      }

      setRows((current) => addSurveyArea(current, response));
    },
    [setRows]
  );

  const pick = useCallback(
    (candidates: SurveyAreaCandidate[]) => {
      setNotice(null);
      setChoices([]);

      if (candidates.length === 0) {
        return;
      }

      // Clicking a section that is already in the rows takes it out again — and
      // that stays true under an overlap, so a pick can always be undone on the
      // map it was made on.
      const picked = candidates.find((candidate) =>
        rowsRef.current.some((row) => row.location?.id === candidate.id)
      );
      if (picked) {
        setRows((current) => removeSurveyArea(current, picked.id));
        return;
      }

      // Most overlaps are one tract stored twice, which is decidable; what is
      // left after narrowing is a real choice between different shapes.
      const narrowed = narrowSurveyAreaCandidates(candidates);

      // A real choice between different shapes: offer them rather than refusing
      // the click, which left nothing to do but search by name (UAT 3).
      if (narrowed.length > 1) {
        setChoices(narrowed);
        return;
      }

      addById(narrowed[0].id);
    },
    [addById, setRows]
  );

  const choose = useCallback(
    (candidate: SurveyAreaCandidate) => {
      setChoices([]);
      addById(candidate.id);
    },
    [addById]
  );

  const dismiss = useCallback(() => {
    setNotice(null);
    setChoices([]);
  }, []);

  return { loading, notice, choices, pick, choose, dismiss };
};
